/**
 * ThreadList.tsx
 *
 * Purpose: Display a list of forum threads with a "New Thread" button.
 * Created: 2026-03-11
 * Author: ralph-manager (Sonnet code loop)
 *
 * WHAT: Render a clickable list of Thread cards; conditionally show ThreadForm for new threads.
 * WHY: Give session players an overview of discussion topics and the ability to start new ones.
 * HOW: Maps the `threads` array to card elements; toggles ThreadForm visibility on button click.
 * WHERE: Rendered by SessionsForum when no thread is selected.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ThreadForm, type ThreadFormPayload } from './ThreadForm';
import type { ThreadData } from './ThreadDetail';

// =============================================================================
// TYPES
// =============================================================================

export interface ThreadListProps {
  /** All threads to display for this session */
  threads: ThreadData[];
  /** Currently selected thread (highlighted) */
  selectedThread: ThreadData | null;
  /** Called when a thread card is clicked */
  onSelectThread: (thread: ThreadData) => void;
  /** Called after a new thread is successfully created */
  onThreadCreated: (thread: ThreadData) => void;
  /**
   * Async handler that performs the API call to create a new thread.
   * Receives the form payload; throws on failure.
   */
  onNewThreadSubmit: (payload: ThreadFormPayload) => Promise<ThreadData>;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * ThreadList Component
 *
 * WHO: Session participants browsing the forum index.
 * WHAT: Thread index with cards showing title, author, and timestamp.
 * WHERE: SessionsForum — default view when no thread is selected.
 * WHY: Allow players to scan active discussions and start new ones.
 */
const ThreadList: React.FC<ThreadListProps> = ({
  threads,
  selectedThread,
  onSelectThread,
  onThreadCreated,
  onNewThreadSubmit,
}) => {
  const [showForm, setShowForm] = useState(false);

  /** Delegate to parent and close form on success. */
  async function handleFormSubmit(payload: ThreadFormPayload) {
    const newThread = await onNewThreadSubmit(payload);
    onThreadCreated(newThread);
    setShowForm(false);
  }

  return (
    <Box>
      {/* Header row */}
      <Flex justify="space-between" align="center" mb={4}>
        <Heading
          as="h2"
          size="md"
          color="#e0e0e0"
          fontFamily="'Cinzel', 'Times New Roman', serif"
        >
          Forum
        </Heading>
        {!showForm && (
          <Button
            bg="#35889A"
            color="white"
            _hover={{ bg: '#2a7080' }}
            size="sm"
            onClick={() => setShowForm(true)}
            fontFamily="'Montserrat', Arial, sans-serif"
          >
            + New Thread
          </Button>
        )}
      </Flex>

      {/* New thread form */}
      {showForm && (
        <Box mb={5}>
          <ThreadForm
            mode="newThread"
            onSubmit={handleFormSubmit}
            onCancel={() => setShowForm(false)}
          />
        </Box>
      )}

      {/* Thread list */}
      {threads.length === 0 ? (
        <Text
          color="#666"
          fontSize="sm"
          fontFamily="'Montserrat', Arial, sans-serif"
          py={4}
          textAlign="center"
        >
          No discussions yet. Start one!
        </Text>
      ) : (
        <VStack spacing={3} align="stretch">
          {threads.map((thread) => {
            const isSelected = selectedThread?.id === thread.id;
            const formattedDate = new Date(thread.created_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });

            return (
              <Box
                key={thread.id}
                bg={isSelected ? '#1e2a30' : '#1a1a22'}
                border="1px solid"
                borderColor={isSelected ? '#35889A' : '#2a2a35'}
                borderRadius="md"
                p={4}
                cursor="pointer"
                onClick={() => onSelectThread(thread)}
                _hover={{ borderColor: '#35889A', bg: '#1e2a30' }}
                transition="border-color 0.15s, background 0.15s"
              >
                <Text
                  fontWeight="bold"
                  color="#e0e0e0"
                  fontFamily="'Cinzel', 'Times New Roman', serif"
                  fontSize="sm"
                  mb={1}
                >
                  {thread.title}
                </Text>
                <Flex justify="space-between">
                  <Text fontSize="xs" color="#35889A" fontFamily="'Montserrat', Arial, sans-serif">
                    {thread.author}
                  </Text>
                  <Text fontSize="xs" color="#666" fontFamily="'Montserrat', Arial, sans-serif">
                    {formattedDate}
                  </Text>
                </Flex>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export { ThreadList };
